/*eslint-disable*/
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import MsgReader from '@kenjiuno/msgreader';
import { readEml } from 'eml-parse-js';
import { DEFAULT_VIEW_ALLOWED_ATTACHMENT_TYPES } from '../../../utils/file.util';

// Import styles
import './DisplayModal.scss';

export default function DisplayModal({
  children,
  isModalOpen,
  toggle,
  errorMessage = '',
  file = null,
  fullscreen = true,
}) {
  // State for .msg parsing
  const [msgData, setMsgData] = useState(null);
  const [htmlBody, setHtmlBody] = useState('');
  const [textBody, setTextBody] = useState('');
  const [emlAtchs, setEmlAtchs] = useState([]);

  function getExt(fname = '') {
    const clean = String(fname || '')
      .trim()
      .toLowerCase();
    const i = clean.lastIndexOf('.');
    return i >= 0 ? clean.slice(i + 1) : '';
  }

  function isPreviewableFile(f) {
    if (!f) return false;
    const allowedTypes = DEFAULT_VIEW_ALLOWED_ATTACHMENT_TYPES.map(
      format => format?.type,
    );
    const allowedExtensions = DEFAULT_VIEW_ALLOWED_ATTACHMENT_TYPES.map(
      format => format?.extension,
    );
    if (f?.type) {
      return allowedTypes.includes(f.type);
    }
    const ext = f?.name?.split('.')?.pop().toLowerCase();
    if (ext) {
      return allowedExtensions.includes(ext);
    }
    return false;
  }

  function reasonNotPreviewable(f) {
    const ext = getExt(f?.name);
    if (!ext) return 'Unsupported file (no extension).';
    return `Unsupported file type: .${ext}`;
  }

  // Helper: reset .msg state
  function clearMsg() {
    setMsgData(null);
    setHtmlBody('');
  }

  // Helper: reset .eml state
  function clearEml() {
    setTextBody('');
    setEmlAtchs([]);
  }

  // Helper: reset everything
  function clearAll() {
    clearMsg();
    clearEml();
    setHtmlBody('');
    setTextBody('');
  }

  // Renders whichever body is available
  function renderEmailBody() {
    if (htmlBody) return <div dangerouslySetInnerHTML={{ __html: htmlBody }} />;
    if (textBody) {
      return <pre style={{ whiteSpace: 'pre-wrap' }}>{textBody}</pre>;
    }
    if (msgData?.bodyText || msgData?.body) {
      const txt = msgData.bodyText || msgData.body;
      return <pre style={{ whiteSpace: 'pre-wrap' }}>{txt}</pre>;
    }
    return <em>No message body found.</em>;
  }

  // Renders .eml attachments
  function renderEmlAttachments() {
    if (!emlAtchs.length) return null;
    return (
      <div style={{ marginTop: 16 }}>
        <h6>Attachments</h6>
        <ul>
          {emlAtchs.map((att, i) => (
            <li key={i}>
              <a
                href={URL.createObjectURL(
                  new Blob([att.data], { type: att?.mime }),
                )}
                download={att?.filename}
              >
                {att?.filename}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Chooses preview mode based on file type
  function renderFilePreview() {
    if (!file) return null;
    const { name, type } = file;
    const lower = name?.toLowerCase();

    // .msg preview
    if (lower?.endsWith('.msg') && msgData) {
      return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          <h5>{msgData?.subject}</h5>
          <p>
            <strong>From:</strong> {msgData.senderName} &lt;
            {msgData?.senderEmail}&gt;
          </p>
          <p>
            <strong>To:</strong>{' '}
            {msgData?.recipients?.map(r => `${r.name} <${r.email}>`).join(', ')}
          </p>
          <hr />
          {renderEmailBody()}
          {msgData?.attachments?.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h6>Attachments</h6>
              <ul>
                {msgData.attachments.map((att, i) => (
                  <li key={i}>
                    <a
                      href={URL.createObjectURL(
                        new Blob([att.contentBytes], { type: att.mimeType }),
                      )}
                      download={att.fileName}
                    >
                      {att.fileName}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    // .eml preview
    if (lower?.endsWith('.eml')) {
      return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          <h5>{file.name}</h5>
          <hr />
          {renderEmailBody()}
          {renderEmlAttachments()}
        </div>
      );
    }

    // Image preview
    if (type?.startsWith('image/')) {
      return (
        <img
          src={URL.createObjectURL(file)}
          alt="Preview"
          style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      );
    }

    // PDF preview
    if (type === 'application/pdf') {
      return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <iframe
            src={URL.createObjectURL(file)}
            title="PDF Preview"
            style={{
              flex: 1,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </div>
      );
    }

    // Fallback unsupported
    return (
      <p style={{ color: 'red' }}>
        Unsupported file type for preview: {type || name}
      </p>
    );
  }

  useEffect(() => {
    if (!file) {
      clearAll();
      return;
    }

    const lower = file?.name?.toLowerCase();

    // Handle .msg
    if (lower.endsWith('.msg')) {
      clearEml();
      (async () => {
        try {
          const buffer = await file.arrayBuffer();
          const reader = new MsgReader(buffer);
          const data = reader.getFileData();
          setMsgData(data);

          // Decode HTML part if exists
          if (data.html && typeof data.html === 'object') {
            const bytes = Object.keys(data.html)
              .map(k => +k)
              .sort((a, b) => a - b)
              .map(i => data.html[i]);
            setHtmlBody(new TextDecoder('utf-8').decode(new Uint8Array(bytes)));
          } else {
            setHtmlBody('');
          }
          setTextBody('');
        } catch (err) {
          clearAll();
        }
      })();
    } else if (lower.endsWith('.eml')) {
      // Handle .eml
      clearMsg();
      (async () => {
        try {
          const raw = await file.text();
          readEml(raw, (err, parsed) => {
            if (err) {
              clearAll();
              return;
            }
            setHtmlBody(parsed.html || '');
            setTextBody(parsed.text || '');
            setEmlAtchs(
              (parsed.attachments || []).map(att => ({
                filename: att.fileName || att.name,
                data:
                  typeof att.data === 'string'
                    ? Uint8Array.from(atob(att.data), c => c.charCodeAt(0))
                    : att.data,
                mime: att.contentType || att.mimeType,
              })),
            );
          });
        } catch (err) {
          clearAll();
        }
      })();
    } else {
      clearAll();
    }
  }, [file]);

  useLayoutEffect(() => {
    if (!isModalOpen || !file) return;
    if (!isPreviewableFile(file)) {
      clearAll();
      if (typeof toggle === 'function') toggle();
    }
  }, [isModalOpen, file]);

  const blocked = isModalOpen && file && !isPreviewableFile(file);
  if (blocked) {
    toast.error(
      'File type not supported for viewing. Supported formats: pdf, jpeg, png, jpg, msg, eml.',
    );
    return null;
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggle={toggle}
      centered
      size="xl"
      fullscreen={fullscreen}
    >
      <div
        style={{
          height: fullscreen ? '100vh' : '90vh', // Changed from maxHeight
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <ModalHeader toggle={toggle}>{errorMessage}</ModalHeader>
        <ModalBody
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {file && renderFilePreview()}
          {children}
        </ModalBody>
      </div>
    </Modal>
  );
}
